/**
 * Cosmic ATC Requirements Confirmation
 * Intercepts Add to Cart and shows a requirements modal if the product
 * has a `custom.requirements` metafield set.
 */
(function () {
  'use strict';

  // ─── Build modal DOM (once) ───────────────────────────────────────────────
  const modal = document.createElement('div');
  modal.id = 'cosmic-atc-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-labelledby', 'cosmic-atc-modal-title');
  modal.setAttribute('aria-describedby', 'cosmic-atc-modal-description');
  modal.hidden = true;
  modal.innerHTML = `
    <div class="cosmic-atc-modal__panel">
      <h2 class="cosmic-atc-modal__title" id="cosmic-atc-modal-title">Before You Continue</h2>
      <p class="cosmic-atc-modal__subtitle" id="cosmic-atc-modal-description">Please confirm you meet the following requirements for this product:</p>
      <ul class="cosmic-atc-modal__list" id="cosmic-atc-modal-list"></ul>
      <div class="cosmic-atc-modal__actions">
        <button type="button" class="button button--primary" id="cosmic-atc-confirm">I Understand — Add to Cart</button>
        <button type="button" class="button button--secondary" id="cosmic-atc-cancel">Cancel</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);

  const listEl      = document.getElementById('cosmic-atc-modal-list');
  const confirmBtn  = document.getElementById('cosmic-atc-confirm');
  const cancelBtn   = document.getElementById('cosmic-atc-cancel');

  let pendingForm   = null;
  let returnFocusElement = null;
  let previousOverflow = '';

  // Keep the short confirmation aligned with the product's published description.
  function descriptionRequirements(form, fallback) {
    const description = form.closest('product-info')?.querySelector('.cosmic-product-description__inner');
    if (!description) return fallback;

    function sectionItems(title) {
      const heading = Array.from(description.querySelectorAll('h2, h3, h4'))
        .find((element) => element.textContent.trim().toLowerCase() === title);
      if (!heading) return [];
      for (let element = heading.nextElementSibling; element; element = element.nextElementSibling) {
        if (/^H[1-6]$/.test(element.tagName)) break;
        if (!element.matches('ul, ol')) continue;
        return Array.from(element.children).map((item) => {
          const copy = item.cloneNode(true);
          // Detailed setup notes stay in the description, referenced below.
          copy.querySelectorAll('em').forEach((note) => note.remove());
          return copy.textContent.replace(/\s+/g, ' ').trim();
        }).filter(Boolean);
      }
      return [];
    }

    const specifications = sectionItems('minimum pc specifications');
    const setups = sectionItems('supported setup configurations');
    if (!specifications.length || !setups.length) return fallback;
    const setupNames = Array.from(new Set(setups.map((setup) => {
      if (/remote play/i.test(setup)) return 'Remote Play';
      return setup.split(/\s*(?:\+| - )\s*/)[0].trim();
    })));
    return [
      specifications.join(' — '),
      `Supported setups: ${setupNames.join(', ')}.`,
      'Check the product description for capture-card and PS5 authentication requirements for your setup.',
    ].join('\n');
  }

  // ─── Open / Close ─────────────────────────────────────────────────────────
  function openModal(form, requirements, submitter) {
    pendingForm    = form;
    returnFocusElement = submitter || form.querySelector('[type="submit"][name="add"]') || document.activeElement;
    previousOverflow = document.body.style.overflow;
    const items = descriptionRequirements(form, requirements)
      .split('\n')
      .map((r) => r.trim())
      .filter(Boolean)
      .map((text) => {
        const item = document.createElement('li');
        item.textContent = text;
        return item;
      });
    listEl.replaceChildren(...items);
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
    trapFocus(modal, confirmBtn);
  }

  function closeModal() {
    if (modal.hidden) return;
    modal.hidden = true;
    document.body.style.overflow = previousOverflow;
    removeTrapFocus(returnFocusElement?.isConnected ? returnFocusElement : null);
    pendingForm = null;
    returnFocusElement = null;
  }

  // ─── Actions ──────────────────────────────────────────────────────────────
  confirmBtn.addEventListener('click', () => {
    const form = pendingForm;
    closeModal();
    if (!form) return;
    form.dataset.cosmicConfirmed = 'true';
    // Use requestSubmit so Shopify's product-form handler runs normally
    const submitBtn = form.querySelector('[type="submit"][name="add"]');
    form.requestSubmit ? form.requestSubmit(submitBtn) : form.submit();
  });

  cancelBtn.addEventListener('click', closeModal);
  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeModal();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !modal.hidden) {
      e.preventDefault();
      closeModal();
    }
  });

  // ─── Intercept submit (capture phase — runs before Shopify's handler) ─────
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (form.dataset.type !== 'add-to-cart-form') return;

      // Already confirmed — let through and clear flag
      if (form.dataset.cosmicConfirmed === 'true') {
        delete form.dataset.cosmicConfirmed;
        return;
      }

      // Read requirements from the parent <product-form> custom element
      const productFormEl  = form.closest('product-form');
      const requirements   = productFormEl && productFormEl.dataset.requirements;
      if (!requirements || !requirements.trim()) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      openModal(form, requirements, e.submitter);
    },
    true // capture phase
  );
})();
